/**
 * Card Component
 * Telegram native card design
 */

import React from 'react';
import styled from 'styled-components';

interface CardStyledProps {
  padding?: 'none' | 'small' | 'medium' | 'large';
  clickable?: boolean;
}

const StyledCard = styled.div<CardStyledProps>`
  background: var(--bg-card);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  transition: all var(--transition);

  padding: ${({ padding }) => {
    switch (padding) {
      case 'none':
        return '0';
      case 'small':
        return 'var(--spacing-sm)';
      case 'large':
        return 'var(--spacing-xl)';
      default:
        return 'var(--spacing-md)';
    }
  }};

  ${({ clickable }) =>
    clickable &&
    `
    cursor: pointer;

    &:hover {
      opacity: 0.9;
    }

    &:active {
      transform: scale(0.99);
    }
  `}
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-md);
`;

const CardTitle = styled.h3`
  font-size: var(--font-lg);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
`;

const CardSubtitle = styled.p`
  font-size: var(--font-sm);
  color: var(--text-secondary);
  margin-top: var(--spacing-xs);
`;

const CardBody = styled.div`
  color: var(--text-primary);
`;

const CardFooter = styled.div`
  margin-top: var(--spacing-md);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

interface CardProps extends CardStyledProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

interface CardComposition {
  Header: typeof CardHeader;
  Title: typeof CardTitle;
  Subtitle: typeof CardSubtitle;
  Body: typeof CardBody;
  Footer: typeof CardFooter;
}

const Card: React.FC<CardProps> & CardComposition = ({
  children,
  onClick,
  clickable,
  padding = 'medium',
  className
}) => {
  return (
    <StyledCard
      onClick={onClick}
      clickable={clickable || !!onClick}
      padding={padding}
      className={className}
    >
      {children}
    </StyledCard>
  );
};

Card.Header = CardHeader;
Card.Title = CardTitle;
Card.Subtitle = CardSubtitle;
Card.Body = CardBody;
Card.Footer = CardFooter;

export default Card;
